/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect, Switch } from 'react-router';
import { ErrorBoundary } from 'src/core/components/ErrorBoundary';
import { PrivateRoute } from 'src/core/components/PrivateRoute';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { routeTable } from 'src/core/utils/routeTable';
import { EntityMigrationPermissionModal } from 'src/luis/appShell/appPage/components/EntityMigrationPermissionModal';
import { BuildPane } from 'src/luis/appShell/buildPane/BuildPane';
import { DashboardPane } from 'src/luis/appShell/DashboardPane';
import { ManagePane } from 'src/luis/appShell/managePane/ManagePane';
import { useCompositeEntityMigration } from 'src/luis/modules/entities/hooks/useCompositeEntityMigration/useCompositeEntityMigration';
import { useCompositeEntityMigrationDependencies } from 'src/luis/modules/entities/hooks/useCompositeEntityMigrationDependencies';
import { useLuisTrainingToastTracker } from 'src/luis/modules/training/useLuisTrainingToastTracker';
import { EntityMigrationModal } from 'src/luis/verticals/entityMigrationModal/EntityMigrationModal';
import { GetStartedGuide } from 'src/luis/verticals/getStartedGuide/GetStartedGuide';
import { IntroSlides } from 'src/luis/verticals/introSlides/IntroSlides';

const defaultModalsState = { isEntityMigrationPermissionModalOpen: false };

export const LuisAppPageContent = observer(() => {
    const { dispatcher, uiStore, rootStore } = useGlobalState();
    const { isXXXLargeOrGreater } = useResponsiveMode();
    const { compositeEntities } = useCompositeEntityMigrationDependencies();
    const { isCompositeEntityMigrationNeeded } = useCompositeEntityMigration();
    useLuisTrainingToastTracker();

    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);

    const isIntroSlidesOpen = !uiStore.teachingBubbles.IntroTutorial;
    const canEntityMigrationPermissionModalOpen = !isIntroSlidesOpen && !uiStore.isEntitiesMigrationModalOpen;

    React.useEffect(() => {
        if (isCompositeEntityMigrationNeeded && compositeEntities.length) {
            setModalsState({ isEntityMigrationPermissionModalOpen: true });
        }
    }, [isCompositeEntityMigrationNeeded, compositeEntities]);

    /**
     * Dismisses the composite entities migration process.
     */
    const suppressEntityMigration = () => {
        setModalsState({ isEntityMigrationPermissionModalOpen: false });
        dispatcher.dispatch('updateEntitiesMigrationModalVisibility', { isVisible: false });
        dispatcher.dispatch('dismissMigration');
    };

    const showEntityMigrationModal = () => {
        setModalsState({ isEntityMigrationPermissionModalOpen: false });
        dispatcher.dispatch('updateEntitiesMigrationModalVisibility', { isVisible: true });
    };

    const dismissEntityMigrationModals = () => {
        setModalsState({ isEntityMigrationPermissionModalOpen: false });
        dispatcher.dispatch('updateEntitiesMigrationModalVisibility', { isVisible: false });
    };

    return (
        <ErrorBoundary>
            <Switch>
                <PrivateRoute path={routeTable.luis.dashboardPage()} component={DashboardPane} />
                <PrivateRoute path={routeTable.luis.buildPage()} component={BuildPane} />
                <PrivateRoute path={routeTable.luis.managePage()} component={ManagePane} />
                <Redirect to={routeTable.luis.buildPage(rootStore.currentLuisAppStore.id, '0.1')} />
            </Switch>

            <IntroSlides
                isOpen={isIntroSlidesOpen}
                onDismiss={() => dispatcher.dispatch('markTeachingBubbleAsViewed', { bubbleName: 'IntroTutorial' })}
            />

            <EntityMigrationPermissionModal
                onDismiss={suppressEntityMigration}
                onSubmit={showEntityMigrationModal}
                compositeNames={compositeEntities.map(entity => entity.name)}
                hidden={!canEntityMigrationPermissionModalOpen || !modalsState.isEntityMigrationPermissionModalOpen}
            />

            <EntityMigrationModal
                compositeEntities={compositeEntities}
                onDismiss={dismissEntityMigrationModals}
                onSuppressMigration={suppressEntityMigration}
                hidden={!uiStore.isEntitiesMigrationModalOpen}
            />

            {isXXXLargeOrGreater && <GetStartedGuide />}
        </ErrorBoundary>
    );
});
